import { Advertiser, CommercialType, LocationType } from '@talon/web/lib/api';
import { IAddress } from '@talon/web/src/utils/decodeGoogle';
import { formatDateOnly } from '@talon/web/src/utils/dateOnly';
import { Box, Button, Text } from 'grommet';
import { ReactNode } from 'react';
import { IMediaAssetLengthMap, IRolePerformer, formatDefaultName } from '..';

interface ISummaryTabProps {
  name: string;
  client?: Partial<Advertiser>;
  product: string;
  budget: number;
  union?: string;
  code: string;
  geography?: string;
  place?: IAddress;
  locationType?: LocationType;
  firstSessionDate?: string;
  exclusivity?: string;
  media?: CommercialType[];
  assetCounts: IMediaAssetLengthMap;
  rolePerformers: IRolePerformer[];
  onEdit: (step: number) => void;
}
const money = (value: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD',
  minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
const Row = ({ label, value }: { label: string; value?: ReactNode }) => <Box
  style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: '4px 12px' }}>
  <Text size="13px" color="#666">{label}</Text>
  <Text size="13px" color={value ? '#222' : '#999'}>{value || 'Not set'}</Text>
</Box>;
const Block = ({ title, step, onEdit, children }: { title: string; step: number; onEdit: (step: number) => void;
  children: ReactNode }) => <Box flex={false} background="white" border={{ color: '#E3E3E3' }} round="10px"
  pad={{ vertical: '12px', horizontal: '14px' }} gap="small">
  <Box direction="row" justify="between" align="center"><Text size="14px" weight={600}>{title}</Text>
    <Button plain label="Edit" color="#0d48a1" onClick={() => onEdit(step)} /></Box>
  {children}
</Box>;

export const SummaryTab = ({ name, client, code, product, budget, union, geography, place, locationType,
  firstSessionDate, exclusivity, media = [], assetCounts, rolePerformers, onEdit }: ISummaryTabProps) => {
  const commercials = media.reduce((sum, medium) => sum + (assetCounts[medium] ?? [30]).length, 0);
  const roles = rolePerformers.reduce((sum, role) => sum + role.groupSize, 0);
  const parts = [firstSessionDate && '1 session', commercials && `${commercials} commercial${commercials === 1 ? '' : 's'}`,
    roles && `${roles} role${roles === 1 ? '' : 's'}`].filter(Boolean);
  const location = geography === 'offshore' ? 'Offshore' :
    [place?.street_address, place?.city, place?.state, place?.zip].filter(Boolean).join(', ');
  const flags = [client?.isSagSignatory && 'SAG signatory', client?.isJpcAuthorizer && 'JPC authorizer']
    .filter(Boolean).join(' · ');
  return <Box flex={false} background="#F7F7F7" pad="14px" gap="10px">
    <Text size="14px" color="#333">This creates {parts.join(', ').replace(/, ([^,]*)$/, ' and $1')}.</Text>
    <Block title="Basics" step={0} onEdit={onEdit}>
      <Row label="Project name" value={name} /><Row label="Client" value={[client?.name, flags].filter(Boolean).join(' · ')} />
      <Row label="Product" value={product} /><Row label="Job code" value={code} />
      <Row label="Talent budget" value={budget ? money(budget) : undefined} />
    </Block>
    <Block title="Shoot" step={1} onEdit={onEdit}>
      <Row label="Location" value={location} />
      <Row label="First session" value={formatDateOnly(firstSessionDate, 'ddd, MMM D, YYYY')} />
      <Row label="Talent travel" value={locationType === LocationType.Distant ? 'Stay overnight' :
        locationType === LocationType.Nearby ? 'Return each day' : undefined} />
      <Row label="Union" value={union === 'union' ? 'Union' : union === 'nonunion' ? 'Non-union' : undefined} />
    </Block>
    <Block title="Media & assets" step={2} onEdit={onEdit}>
      <Row label="Exclusivity" value={exclusivity === 'yes' ? 'Yes' : exclusivity === 'no' ? 'No' : undefined} />
      {media.map((medium) => <Row key={medium} label={medium}
        value={(assetCounts[medium] ?? [30]).map((length) => `${length}s`).join(', ')} />)}
    </Block>
    <Block title="Roles" step={4} onEdit={onEdit}>
      <Box style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr 1.3fr 50px', gap: 8 }}>
        {['Name', 'Role type', 'Performer type', 'Count'].map((label) =>
          <Text key={label} size="12px" color="#666">{label}</Text>)}
        {rolePerformers.map((role, index) => [formatDefaultName(role), role.roleType, role.performerType,
          role.groupSize].map((value, col) => <Text key={`${index}-${col}`} size="13px" color="#222">{value}</Text>))}
      </Box>
    </Block>
  </Box>;
};
